import { useEffect } from 'react';
import useGlobalContext from "../../context/useGlobalContext";
import useDisplaySnackbar from '../../utils/useDisplaySnackbar';
import ClientAPIMeetings from "../../api/clientAPI";
import React, { useState } from 'react';
import { Box } from '@mui/material';
import Datatable from "../../components/Common/Datatable";
import { getColumns, initialSort } from "../Meetings/meetingsTableConfig";
import { MeetingType1, MeetingType2 } from '../../utils/types';
import { getUTCTimeZoneOffset } from '../../utils/utils';
import dayjs from 'dayjs';
import ProgressSpinner from '../Common/ProgressSpinner';
import { getUserAttributes } from '../../utils/auth';

const generateRows = (meetings: any) =>
    meetings.map((item: any) => ({
        id: item.id,
        title: item.summary,
        start_time: item.start_time,
        duration: item.duration,
        no_of_attendees: item.no_of_attendees,
        is_external: item.is_external,
        meetingType2:
            item.no_of_attendees === 0
                ? MeetingType2.FocusTime
                : item.no_of_attendees === 2
                    ? MeetingType2.OneToOne
                    : MeetingType2.Group,
        totalActions: item.total_action,
        completedActions: item.completed_action,
        attendees: item.attendees,
        organizer: item.organizer,
        meeting_sentiment: item.meeting_sentiment, // New field for sentiment
        rating_source: item.rating_source,

    }));

const ClientSearchMeetingView


: React.FC<{ clientDomain: string }> = ({ clientDomain }) => {
    const [columns] = useState<any[]>(getColumns());
    const [rows, setRows] = useState<any[]>([]);
    const [totalRecord, setTotalRecord] = useState(0);
    const { calendar, startEndTime, timeRange } = useGlobalContext();
    const { showSnackbar } = useDisplaySnackbar();
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState<string>("");
    const [sort, setSort] = useState(initialSort);
    const today = new Date();
    const endOfToday = new Date(today);
    endOfToday.setHours(today.getHours(), today.getMinutes(), 0, 0);
    const startOfSixMonthsAgo = new Date(today);
    startOfSixMonthsAgo.setMonth(today.getMonth() - 12);
    startOfSixMonthsAgo.setHours(0, 0, 0, 0);
    const loggedInEmail = getUserAttributes()?.email;
    const { startEndMeetingTime } = useGlobalContext();
    const enabled = !!loggedInEmail;
    const {
        isLoading: isDataLoading,
        isFetching: isDataFetching,
        isSuccess,
        isError,
        error,
        data: meetingsData,
    } = ClientAPIMeetings.useGetTeamInfoMeetings(
        {
            start_time: startEndMeetingTime?.start_time,
            end_time: startEndMeetingTime?.end_time,
            client_domain: clientDomain,
            page: pageNumber,
            per_page: pageSize,
            search:search,
            sort: sort,
        },
        enabled
    );
    useEffect(() => {
        setShowSpinner(isDataLoading || isDataFetching);

        if (isSuccess) {
            const meetingList = meetingsData.data?.data?.meetings;
            setTotalRecord(meetingsData.data.total_records);
            if (meetingList) {
                setRows(generateRows(meetingList));
            }
        }
        if (isError) {
            showSnackbar("error", error);
        }
    }, [isDataLoading, isDataFetching, isSuccess, isError, meetingsData]);

    const onPageChange = (value: number) => {
        setPageNumber(value);
    };

    const onPaginationChange = (pageNumber: number) => {
        onPageChange(pageNumber + 1);
    };

    const onSearch = (value: string) => {
        setSearch(value);
        setPageNumber(1);
    };

    const onGlobalFilterChange = (globalFilter: any) => {
        onSearch(globalFilter);
    };

    const onFilter = () => {
        setPageNumber(1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
      };

    const onFilterChange = (columnFilter: any[]) => {
        const filterSelections: {
            date: { start_time: string; end_time: string } | undefined;
            is_external: undefined | boolean;
            group_type: string;
        } = {
            date: undefined,
            is_external: undefined,
            group_type: "",
        };

        columnFilter.forEach((filter) => {
            if (filter.id === "start_time") {
                const utcTimeOffset = getUTCTimeZoneOffset();
                const formattedStartDate = `${dayjs(filter.value).format("YYYY-MM-DD")}T00:00:00${utcTimeOffset}`;
                const formattedEndDate = `${dayjs(filter.value).format("YYYY-MM-DD")}T23:59:59${utcTimeOffset}`;
                filterSelections.date = {
                    start_time: formattedStartDate,
                    end_time: formattedEndDate,
                };
            }
            if (filter.id === "meetingType1") {
                if (filter.value === MeetingType1.Internal)
                    filterSelections.is_external = false;
                if (filter.value === MeetingType1.External)
                    filterSelections.is_external = true;
            }
            if (filter.id === "meetingType2") {
                if (filter.value === MeetingType2.FocusTime)
                    filterSelections.group_type = "focus_time";
                if (filter.value === MeetingType2.OneToOne)
                    filterSelections.group_type = "one_to_one";
                if (filter.value === MeetingType2.Group)
                    filterSelections.group_type = "group";
            }
        });
        onFilter();
    };

    const onSortingChange = (sorting: any[]) => {
        if (sorting && sorting.length > 0)
            setSort({
                sort_field: sorting[0].id,
                sort_order: sorting[0].desc === true ? "desc" : "asc",
            });
    };
    return (
        <>
            <Box
                className="flex flex-col justify-start items-start w-full"
                sx={{ gap: "12px" }}
            >
                <Box className="w-full">
                    <Datatable
                        // header={"Meetings"}
                        columns={columns}
                        rows={rows}
                        rowCount={totalRecord}
                        pageSize={pageSize}
                        initialSort={sort}
                        onPaginationChange={onPaginationChange}
                        onSortingChange={onSortingChange}
                        onFilterChange={onFilterChange}
                        onGlobalFilterChange={onGlobalFilterChange}
                        startEndTime={startEndTime}
                        timeRange={timeRange}
                        calendarId={calendar?.calendar_id}
                        onPageSizeChange={(newPageSize: number | null | undefined) => {
                            if (newPageSize !== null && newPageSize !== undefined) {
                              handlePageSizeChange(newPageSize);
                            }
                        }}
                        columnVisibility={{ actual_duration: false, sharedDate: false, abstractiveScore: false, shareIcon: false}}
                    />
                </Box>
                <ProgressSpinner showSpinner={showSpinner} />
            </Box>
        </>
    );
};

export default ClientSearchMeetingView;